import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { tournamentsService } from 'v2/api/tournamentsService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { USER_ROLES, UserRole } from 'v2/constants';
import { PayoutBalanceForm, TournamentModel } from 'v2/types/TournamentModel';
import { handleHttpError } from 'v2/utils';

interface PayoutBalanceDialogProps {
  tournament: TournamentModel;
  isOpen: boolean;
  onClose: VoidFunction;
}

export const PayoutBalanceDialog: FC<PayoutBalanceDialogProps> = ({ tournament, isOpen, onClose }) => {
  const { trigger: triggerUpdateBalance, isMutating: isMutatingBalance } = useSWRMutation(
    ['updateUsersTournamentBalance', tournament.id],
    tournamentsService.updateUsersTournamentBalance,
    {
      onSuccess: () => {
        toast.success("User's balances have been updated");
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerUpdateBalanceByLevel, isMutating: isMutatingByLevel } = useSWRMutation(
    ['updateUsersTournamentBalanceByLevel', tournament.id],
    tournamentsService.updateUsersTournamentBalanceByLevel,
    {
      onSuccess: () => {
        toast.success("User's balances have been updated");
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<PayoutBalanceForm>({
    initialValues: { balance: 0, roles: [UserRole.DEFAULT_USER] },
    onSubmit: (values) => {
      triggerUpdateBalance(values);
    },
  });

  const handleChangeRoles = (values: UserRole[]) => {
    formik.setFieldValue('roles', values);
  };

  const handlePayoutByLevel = () => {
    triggerUpdateBalanceByLevel(formik.values);
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Top Up Tournament Balance</DialogTitle>

      <DialogContent>
        <form className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="balance"
            value={formik.values.balance}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Balance"
            type="number"
            required
          />

          <CheckBoxAutocomplete
            label="Roles"
            value={formik.values.roles}
            options={USER_ROLES}
            onChange={handleChangeRoles}
            required
          />

          <div className="form-group">
            <Button
              loading={isMutatingBalance}
              disabled={isMutatingByLevel}
              variant="contained"
              size="large"
              type="submit"
              fullWidth
            >
              Top Up Balances
            </Button>

            <Button
              onClick={handlePayoutByLevel}
              loading={isMutatingByLevel}
              disabled={isMutatingBalance}
              variant="outlined"
              size="large"
              type="button"
              fullWidth
            >
              Top Up By Level
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
